/**
 * The page every published customer site links to.
 *
 * ⚠️ IT IS REACHED BY SOMEBODY WHO IS NOT A CUSTOMER AND NEVER WILL BE. Everything else in this app
 * talks to a person building a site; this talks to a stranger who has landed on one and thinks it
 * should not exist. So it asks for nothing, requires no sign-in, and puts the address to write to
 * above the explanation rather than below it — a reporting page that makes somebody read three
 * paragraphs before it tells them where to send anything is a reporting page that loses reports.
 *
 * ⚠️ THE SITE BEING REPORTED ARRIVES IN THE QUERY STRING, from the footer badge. It is shown back so
 * the person can see what they are reporting, and pre-filled into the mail link so a report that
 * says only "this is a scam" still identifies its subject. It is rendered as TEXT, never as a link:
 * a page on our domain that turns an arbitrary query parameter into a clickable link to a site
 * somebody is reporting as malicious is a redirector for that site.
 */

import { useSearchParams } from 'react-router'
import { Card, Notice, useLang } from '@ksa/ui'
import { isHandle } from '@ksa/model'
import { useTitle } from '../useTitle.ts'

/**
 * ⚠️ ONE DECLARATION, BECAUSE IT WILL CHANGE. A personal mailbox published on every route into this
 * product will be scraped; the intended replacement is a role alias (`abuse@ksa-web.app`, forwarded
 * by Cloudflare Email Routing) which can be rotated without touching anything else. Keeping the
 * address in one constant is what makes that a one-line change rather than a search.
 */
const REPORT_EMAIL = 'bjorn.a.goransson@gmail.com'

export function Report() {
  const { t, lang } = useLang()
  useTitle('Report a site', 'الإبلاغ عن موقع')
  const [params] = useSearchParams()

  // ⚠️ VALIDATED, NOT TRUSTED. `site` comes from a URL a stranger can write. `isHandle` is the same
  // rule the routing layer applies, so anything that is not a real handle shape is simply not shown
  // — which also means no mail link can be built out of it.
  const raw = params.get('site') ?? ''
  const handle = isHandle(raw) ? raw : null

  const subject = handle ? `Report: ${handle}.ksa-web.app` : 'Report a site on ksa-web.app'
  const body = handle
    ? `Site: https://${handle}.ksa-web.app\n\n${t('What is wrong with it:', 'ما المشكلة في هذا الموقع:')}\n`
    : `${t('Which site:', 'أي موقع:')}\n\n${t('What is wrong with it:', 'ما المشكلة فيه:')}\n`
  const mailto = `mailto:${REPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`

  return (
    <div className="u-page u-stack">
      <header className="u-stack u-stack--tight">
        <h1 className="u-display">{t('Report a site', 'الإبلاغ عن موقع')}</h1>
        <p className="u-lead">
          {t(
            'Anyone can build a website here. If one of them is impersonating a business, selling something that is not real, or otherwise should not be online, tell us and we will look at it.',
            'يستطيع أي شخص إنشاء موقع هنا. إذا كان أحد المواقع ينتحل هوية جهة، أو يبيع شيئًا غير حقيقي، أو لا ينبغي أن يكون منشورًا، أخبرنا وسننظر فيه.',
          )}
        </p>
      </header>

      {handle && (
        <Notice title={t('The site you came from', 'الموقع الذي جئت منه')}>
          {/* Text, not a link. See the note at the top of this file. */}
          <span className="u-mono" dir="ltr">
            <span className="u-hostname">{handle}.ksa-web.app</span>
          </span>
        </Notice>
      )}

      <Card raised>
        <div className="u-stack">
          <h2 className="u-heading">{t('How to report it', 'كيف تُبلغ')}</h2>
          <p>
            {t('Write to', 'راسلنا على')}{' '}
            <a className="u-link" href={mailto} dir="ltr">
              {REPORT_EMAIL}
            </a>
            {t(
              '. Include the address of the site and what is wrong with it. A screenshot helps.',
              '. أرفق عنوان الموقع وما المشكلة فيه. لقطة الشاشة تساعد.',
            )}
          </p>
          <p className="u-small">
            {t(
              'A real person reads these. If a site is impersonating someone or trying to take payments or passwords, say so — those are looked at first.',
              'يقرأ هذه الرسائل شخص حقيقي. إذا كان الموقع ينتحل هوية أحد أو يحاول أخذ مدفوعات أو كلمات مرور، فاذكر ذلك — تُراجَع هذه الحالات أولًا.',
            )}
          </p>
        </div>
      </Card>

      <Card>
        <div className="u-stack">
          <h2 className="u-heading">{t('What sites here can and cannot do', 'ما يمكن لهذه المواقع فعله وما لا يمكن')}</h2>
          <p>
            {t(
              'Sites built here are static pages. They cannot collect passwords, card numbers or any other information: there is no form, no login box and no payment field anywhere in what this product can build, and the build refuses to add one.',
              'المواقع المبنية هنا صفحات ثابتة. لا يمكنها جمع كلمات المرور أو أرقام البطاقات أو أي معلومات أخرى: لا يوجد نموذج ولا حقل دخول ولا حقل دفع في أي مما يبنيه هذا المنتج، والبناء يرفض إضافة واحد.',
            )}
          </p>
          <p>
            {t(
              'So a page here asking you to type anything is not a page from this product. What a site here can still do is claim to be someone it is not — that is what this page is for.',
              'لذلك فإن أي صفحة هنا تطلب منك كتابة شيء ليست من هذا المنتج. لكن ما يزال بإمكان الموقع أن يدّعي أنه جهة أخرى — ولهذا وُجدت هذه الصفحة.',
            )}
          </p>
        </div>
      </Card>

      <Card>
        <div className="u-stack">
          <h2 className="u-heading">{t('If you own the business being copied', 'إذا كنت صاحب النشاط المنتحَل')}</h2>
          <p>
            {t(
              'Say so in your message and include something that shows the connection — your own domain, a company registration, anything reasonable. Impersonation of a real business or a government service is removed rather than argued about.',
              'اذكر ذلك في رسالتك مع ما يثبت الصلة — نطاقك الخاص أو السجل التجاري أو أي شيء معقول. انتحال نشاط حقيقي أو جهة حكومية يُزال ولا يُناقش.',
            )}
          </p>
        </div>
      </Card>

      <p className="u-small" lang={lang}>
        {t(
          'This page is part of ksa-web.app, the tool the site was built with. It is not run by the business whose site you were looking at.',
          'هذه الصفحة جزء من ksa-web.app، الأداة التي بُني بها الموقع. ولا يديرها النشاط الذي كنت تتصفح موقعه.',
        )}
      </p>
    </div>
  )
}
